import Chevron from 'components/Chevron';
import GlowBar from 'components/GlowBar';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { ordinalSuffixOf } from 'utils/common';
import { colors } from 'utils/constants';
import LeaderboardDetailsChooser from './LeaderboardDetailsChooser';
import { resizeImageUrl } from 'utils/image/imageResizeService';

const textForLeaderboardAction = (leaderboardAction: string) => {
  switch (leaderboardAction) {
    case 'REFER_FRIEND':
      return 'REFER & EARN £12';

    case 'REFER_FRIEND_TOKEN_REWARD':
      return 'Refer a  friend (non-Premium)';

    case 'TRUSTPILOT_REVIEW':
      return 'Write Trustpilot review';

    case 'TRUSTPILOT_REVIEW_CARRIED_OVER':
      return 'Write Trustpilot review (carried over)';

    case 'FACEBOOK_REVIEW':
      return 'Write Facebook review';

    case 'FACEBOOK_REVIEW_CARRIED_OVER':
      return 'Write Facebook review (carried over)';

    case 'GOOGLE_REVIEW':
      return 'Write Google review';

    case 'GOOGLE_REVIEW_CARRIED_OVER':
      return 'Write Google review (carried over)';

    case 'INSTAGRAM_FOLLOW':
      return 'Follow DAYMADE on Instagram';

    case 'INSTAGRAM_FOLLOW_CARRIED_OVER':
      return 'Follow DAYMADE on Instagram (carried over)';

    case 'LOTTERY_ENTRY':
      return 'Enter Weekly Draw';

    case 'WEEKLY_DRAW_FREEMIUM':
      return 'Enter Weekly Draw (free)';

    case 'RAFFLE_ENTRY':
      return 'Enter raffle';

    case 'DAILY_POUND_RAFFLE':
      return 'Enter Pound Raffle';

    case 'SUGGEST_PRIZE':
      return 'Suggest prize';

    case 'ENTER_DAILY_CHALLENGER':
      return 'Enter Free Daily Draw';

    case 'EXCHANGE_TOKEN':
      return 'Redeem DAYMADE Token';

    case 'VIDEO_TESTIMONIAL':
      return 'Video Testimonial';

    case 'SUBSCRIBER_BONUS':
      return 'Subscriber Bonus';

    case 'SUBSCRIBER_BONUS_CARRIED_OVER':
      return 'Subscriber Bonus (carried over)';

    case 'GENERIC_BONUS':
      return 'Bonus';

    case 'AJ385_SURVEY':
      return 'Complete Feedback Survey';

    case "SPIN_TO_WIN":
      return "Spin to Win";

    default:
      return '';
  }
};

interface IDashboardBarProps {
  pointsBreakdown: {
    id: string;
    created_at: Date;
    action: string;
    points: number;
  }[];
}

const DashboardBar = observer(({ pointsBreakdown }: IDashboardBarProps) => {
  const { currentUser, leaderboardStats, updateCurrentUser } = useUserStore();

  const [expanded, setExpanded] = useState(true);
  const [showLeaderboardDetailsChooser, setShowLeaderboardDetailsChooser] =
    useState(false);

  const [pointsSectionExpanded, setPointsSectionExpanded] = useState(false);

  if (!currentUser || !leaderboardStats) return null;

  const pointsTable = (
    <div
      style={{
        maxHeight: '40vh',
        overflowY: 'scroll',
        marginTop: '1em',
        padding: '0 1em',
      }}
    >
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          border: 'none',
          maxWidth: '800px',
          margin: '0 auto',
        }}
      >
        <thead>
          <tr style={{ borderTop: 0 }}>
            <th>
              <h5
                style={{
                  margin: 0,
                  fontSize: '1em',
                  textAlign: 'left',
                  fontWeight: 800,
                }}
              >
                ACTION
              </h5>
            </th>
            <th>
              <h5
                style={{
                  margin: 0,
                  fontSize: '1em',
                  textAlign: 'center',
                  fontWeight: 800,
                }}
              >
                DATE
              </h5>
            </th>
            <th>
              <h5
                style={{
                  margin: 0,
                  fontSize: '1em',
                  textAlign: 'center',
                  fontWeight: 800,
                }}
              >
                POINTS
              </h5>
            </th>
          </tr>
        </thead>

        <tbody style={{ maxWidth: '600px' }}>
          {pointsBreakdown.map((action) => (
            <tr key={action.id}>
              <td style={{ textAlign: 'left' }}>
                {textForLeaderboardAction(action.action)}
              </td>

              <td style={{ textAlign: 'center' }}>
                {action.created_at.toLocaleDateString()}
              </td>

              <td style={{ textAlign: 'center' }}>
                {action.points.toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <style jsx>{`
        tr {
          border-top: 3px solid #eee;
        }

        td {
          font-weight: 600;
        }

        th {
          position: sticky;
          top: 0;
          background-color: white;
        }

        th,
        td {
          padding: 1em 0.5em;
        }
      `}</style>
    </div>
  );

  return (
    <div className="container">
      <GlowBar
        color="rgba(0, 0, 0, 0.2)"
        direction="up"
        className={undefined}
        style={undefined}
      />

      <div
        style={{
          width: '100%',
          cursor: 'pointer',
        }}
        onClick={() => {
          if (expanded) setPointsSectionExpanded(false);
          setExpanded(!expanded);
        }}
      >
        <h2
          style={{
            fontWeight: 'normal',
            fontSize: '30px',
            position: 'absolute',
            right: 0,
            color: '#AAA',
          }}
        >
          {expanded ? '-' : '+'}
        </h2>

        <h5
          style={{
            textShadow: `2px 2px ${colors.brand.pastel}`,
            margin: 0,
            padding: '1.5em',
          }}
        >
          YOUR <strong>DASHBOARD</strong>
        </h5>
      </div>

      {expanded && (
        <>
          <div
            className="separator"
            style={{
              margin: '0 auto',
              width: '90%',
              height: '2px',
              maxWidth: '600px',
            }}
          />

          {showLeaderboardDetailsChooser && (
            <LeaderboardDetailsChooser
              isLeaderboardPage
              onSuccess={async () => {
                await updateCurrentUser();
                setShowLeaderboardDetailsChooser(false);
              }}
              onClose={() => setShowLeaderboardDetailsChooser(false)}
            />
          )}

          <div className="detailsContainer">
            <div
              onClick={() => setShowLeaderboardDetailsChooser(true)}
              className="initialsCircle"
              style={{
                margin: '0.5em',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <span className="initials">
                {currentUser.firstName.length > 0 && currentUser.firstName[0]}
                {currentUser.lastName.length > 0 && currentUser.lastName[0]}
              </span>
              {currentUser.avatarLocation && (
                <img
                  src={resizeImageUrl(currentUser.avatarLocation, { width: 80, height: 80 })}
                  style={{
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                  }}
                />
              )}
              <div
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  position: 'absolute',
                  bottom: 0,
                  left: 'calc(50% - 20px)',
                  width: '100%',
                  textAlign: 'center',
                  fontWeight: 600,
                }}
              >
                edit
              </div>
            </div>

            <div
              className="separator"
              style={{
                width: '2px',
                height: '40px',
                margin: '1em',
                marginRight: '1em',
                flexShrink: 0,
              }}
            />

            <h5
              style={{
                margin: 0,
                flexGrow: 1,
                flexShrink: 1,
                wordBreak: 'break-all',
              }}
            >
              {currentUser.friendlyName}
            </h5>

            <>
              <div
                className="separator"
                style={{
                  width: '2px',
                  height: '40px',
                  margin: '1em',
                  marginRight: '1em',
                  flexShrink: 0,
                }}
              />
              <p
                style={{
                  fontWeight: 600,
                  margin: '1em',
                  flexShrink: 0,
                  // @ts-ignore
                  wordWrap: 'wrap',
                }}
              >
                {leaderboardStats.rank !== null && (
                  <>
                    Rank:{' '}
                    <strong>
                      {leaderboardStats.rank > 200 ? (
                        '>200'
                      ) : (
                        <>
                          {leaderboardStats.rank}
                          <sup>{ordinalSuffixOf(leaderboardStats.rank)}</sup>
                        </>
                      )}
                    </strong>
                    <br />
                  </>
                )}
                Points: <strong>{leaderboardStats.pointsTotal}</strong>
                <br />
                Raffle entries:{' '}
                <strong>{leaderboardStats.raffleEntries}</strong>
              </p>
            </>
          </div>

          {pointsBreakdown.length > 0 && (
            <div
              onClick={() => setPointsSectionExpanded(!pointsSectionExpanded)}
              style={{ marginBottom: '0.8em' }}
              className="moreLessButton"
            >
              {pointsSectionExpanded ? 'HIDE' : 'SEE'} MY POINTS
              <Chevron
                direction="down"
                reversed={pointsSectionExpanded}
                style={{
                  color: colors.primaryBrandGray,
                  fontSize: '1em',
                  marginRight: '1em',
                  height: 'auto',
                }}
                inline={undefined}
              />
            </div>
          )}

          {pointsSectionExpanded && pointsTable}
        </>
      )}

      <style jsx>{`
        .container {
          position: fixed;
          bottom: 0;
          width: 100%;
          background-color: white;
          z-index: 5;
        }

        .detailsContainer {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 1em;
          max-width: 600px;
          margin: 0 auto;
        }

        .initialsCircle {
          width: 40px;
          height: 40px;
          background-color: ${colors.primaryBrandGray};
          border-radius: 20px;
          box-shadow: 0 0 5px ${colors.brand.neon};
          display: flex;
          flex-shrink: 0;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }

        .initials {
          font-size: 1.3em;
          font-weight: bold;
          -webkit-text-fill-color: transparent;
          -webkit-text-stroke-width: 1.5px;
          -webkit-text-stroke-color: #59ffde;
          text-shadow: 0 0 12px rgba(155, 155, 255, 0.3);
        }

        .moreLessButton {
          display: flex;
          flex-direction: column;
          justify-content: flex-center;
          background-color: clear;
          font-weight: bold;
          letter-spacing: 0.3em;
          cursor: pointer;
          align-items: center;
          text-align: center;
        }
      `}</style>
    </div>
  );
});

export default DashboardBar;
